import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../../supabase';
import Button from '../../../../components/common/Button';
import styles from './InvestmentModal.module.css';
import { ICON_PATHS } from '../../../../constants/iconPaths';
import { Chip } from '../../../../components/common/Chip';

function InvestmentModal({ 
  isOpen, 
  onClose, 
  idea, 
  userProfile, 
  onSuccess 
}) {
  const [investmentAmount, setInvestmentAmount] = useState(0);
  const INVESTMENT_UNIT = 100000;
  const [startY, setStartY] = useState(null);
  const [currentY, setCurrentY] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // 모달이 열리면 body 스크롤 비활성화
      document.body.style.overflow = 'hidden';
    } else {
      // 모달이 닫히면 body 스크롤 활성화
      document.body.style.overflow = 'auto';
    }

    // 컴포넌트 언마운트 시 body 스크롤 활성화
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const handleIncrease = () => {
    if (userProfile?.current_budget && investmentAmount + INVESTMENT_UNIT <= userProfile.current_budget) {
      setInvestmentAmount(prevAmount => prevAmount + INVESTMENT_UNIT);
    }
  };

  const handleDecrease = () => {
    if (investmentAmount - INVESTMENT_UNIT >= 0) {
      setInvestmentAmount(prevAmount => prevAmount - INVESTMENT_UNIT);
    }
  };

  const handleClose = () => {
    setInvestmentAmount(0);
    onClose();
  };

  // 예상 잔액 계산
  const expectedBalance = userProfile?.current_budget ? userProfile.current_budget - investmentAmount : 0;

  const handleInvest = async () => {
    try {
      if (!userProfile?.user_id) {
        alert('사용자 정보를 찾을 수 없습니다.');
        return;
      }

      if (investmentAmount <= 0) {
        alert('투자 금액을 입력해주세요.');
        return;
      }

      if (investmentAmount > userProfile.current_budget) {
        alert('보유한 예산보다 많은 금액을 투자할 수 없습니다.');
        return;
      }

      const { data, error: investmentError } = await supabase
        .from('idea_investments')
        .insert({
          user_id: userProfile.user_id,
          idea_id: idea.id,
          investment_amount: investmentAmount
        })
        .select();

      if (investmentError) {
        alert('투자 중 오류가 발생했습니다.');
        return;
      }

      setInvestmentAmount(0);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Investment error:', error);
      alert('투자 중 오류가 발생했습니다.');
    }
  };

  const handleTouchStart = (e) => {
    setStartY(e.touches[0].clientY);
  };

  const handleTouchMove = (e) => {
    if (!startY) return;
    
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY;
    
    // 위로 드래그할 때는 무시 (diff < 0)
    if (diff > 0) {
      setCurrentY(diff);
      if (modalRef.current) {
        modalRef.current.style.transform = `translateY(${diff}px)`;
        modalRef.current.style.transition = 'none'; // 드래그 중에는 트랜지션 제거
      }
    }
  };

  const handleTouchEnd = () => {
    if (!startY || !currentY) return;

    if (currentY > 100) {
      // 일정 거리 이상 드래그하면 모달 닫기
      setIsClosing(true);
      if (modalRef.current) {
        modalRef.current.style.transform = 'translateY(100%)';
        modalRef.current.style.transition = 'transform 0.3s ease-out';
      }
      // 애니메이션이 끝난 후 모달 닫기
      setTimeout(() => {
        onClose();
        setIsClosing(false);
      }, 300);
    } else {
      // 원위치로 돌아가기
      if (modalRef.current) {
        modalRef.current.style.transform = 'translateY(0)';
        modalRef.current.style.transition = 'transform 0.2s ease-out';
      }
    }

    setStartY(null);
    setCurrentY(null);
  };

  const handleQuickInvestment = (amount) => {
    const newAmount = investmentAmount + amount;
    if (userProfile?.current_budget && newAmount <= userProfile.current_budget) {
      setInvestmentAmount(newAmount);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className={`${styles.overlay} ${isClosing ? styles.fadeOut : ''}`} 
        onClick={onClose} 
      />
      <div 
        ref={modalRef}
        className={`${styles.modal} ${isClosing ? styles.slideDown : ''}`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className={styles.modalContent}>
          <div className={styles.budgetInfo}>
            <div className={styles.investmentAmount}>
              {investmentAmount.toLocaleString()}원
            </div>
            <p className={styles.expectedBalance}>
              잔액: {expectedBalance.toLocaleString()}원
            </p>
          </div>
          <div className={styles.investmentControl}>
            <button 
              className={styles.controlButton}
              onClick={handleDecrease}
              disabled={investmentAmount <= 0}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d={ICON_PATHS.minus} fill="currentColor" />
              </svg>
            </button>
            <button 
              className={styles.controlButton}
              onClick={handleIncrease}
              disabled={!userProfile?.current_budget || investmentAmount + INVESTMENT_UNIT > userProfile.current_budget}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d={ICON_PATHS.plus} fill="currentColor" />
              </svg>
            </button>
          </div>
          <div className={styles.chipContainer}>
            <Chip
              label="+100만원"
              onClick={() => handleQuickInvestment(1000000)}
              disabled={!userProfile?.current_budget || investmentAmount + 1000000 > userProfile.current_budget}
            />
            <Chip
              label="+1000만원"
              onClick={() => handleQuickInvestment(10000000)}
              disabled={!userProfile?.current_budget || investmentAmount + 10000000 > userProfile.current_budget}
            />
          </div>
          <div className={styles.modalButtons}>
            <Button 
              variant="primary"
              size="large"
              width="full-width"
              onClick={handleInvest}
              disabled={investmentAmount <= 0}
            >
              투자하기
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

export default InvestmentModal; 